import React, { useCallback, useEffect, useMemo, useRef, useState, VFC } from 'react';
import { Helmet } from 'react-helmet';
import { Nullable } from 'src/types/nullable.type';
import JourneySlideover from 'src/common/JourneySlideOver';
import {
  DashboardSettingsManageWorkspacesForm,
  EditableWorkspaceForm,
} from 'src/dashboard/settings/manage-workspaces/form';
import { DashboardSettingsManageWorkspacesList } from 'src/dashboard/settings/manage-workspaces/list';
import { useCurrentOrganization } from 'src/store/organization';
import { apiCreateWorkspace, apiDeleteWorkspace, apiUpdateWorkspace } from 'src/utils/journeyApi';
import { Organization } from 'src/common/interfaces/organization.interface';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { ErrorInterface } from 'src/common/interfaces/error.interface';
import { EventBus } from 'src/common/EventBus';
import { Workspace } from 'src/common/interfaces/workspace.interface';
import { useCurrentUser } from 'src/store/user';
import { isPrivateWorkspace, isTeamWorkspace } from 'src/utils/journey';

interface DashboardSettingsManageWorkspacesProps {
  currentOrganization: Organization;
}

const emptyWorkspaceForm = { name: '', user_ids: [], id: null, editingWorkspaceId: null };

export const DashboardSettingsManageWorkspaces: VFC<DashboardSettingsManageWorkspacesProps> = ({
  currentOrganization,
}) => {
  const { getMemberWorkspaces, createWorkspace, updateWorkspace, deleteWorkspace } = useCurrentOrganization();
  const [showSlideOver, setShowSlideOver] = useState(false);
  const { currentUser } = useCurrentUser();
  const workspaces: Workspace[] = getMemberWorkspaces(currentUser);
  const [activeWorkspace, setActiveWorkspace] = useState<Nullable<Workspace>>(null);
  const activeWorkspaceRef = useRef<EditableWorkspaceForm>({ ...emptyWorkspaceForm });
  const { setSuccessNotification, setErrorNotification } = useNotificationMessage();
  const [showWorkspacePanel, setShowWorkspacePanel] = useState(false);

  const onWorkspacePanelOpened = useCallback(() => {
    setShowWorkspacePanel(true);
    setTimeout(() => {
      setShowSlideOver(true);
    }, 50);
  }, []);

  const onWorkspacePanelClosed = useCallback(() => {
    setShowSlideOver(false);
    setTimeout(() => {
      setShowWorkspacePanel(false);
    }, 50);
  }, []);

  const onManageWorkspaceFormSubmitted = useCallback(
    async (values: Record<string, string>) => {
      try {
        let request = () => apiCreateWorkspace(currentOrganization.id, values);
        let stateUpdaterAdaptor = createWorkspace;
        let successMessageEnd = 'created';

        if (activeWorkspace) {
          request = () => apiUpdateWorkspace(activeWorkspace.id, values);
          stateUpdaterAdaptor = updateWorkspace;
          successMessageEnd = 'updated';
        }

        const response = await request();
        stateUpdaterAdaptor(response.workspace);

        setSuccessNotification(`Workspace is ${successMessageEnd}`);
        onWorkspacePanelClosed();
      } catch (err) {
        setErrorNotification((err as ErrorInterface).message);
      }
    },
    [
      activeWorkspace,
      updateWorkspace,
      createWorkspace,
      onWorkspacePanelClosed,
      setSuccessNotification,
      currentOrganization,
      setErrorNotification,
    ]
  );

  useEffect(() => {
    const onWorkspaceEditClicked = (rowIndex: number) => {
      const workspace: Workspace = workspaces[rowIndex];

      setActiveWorkspace(workspace);

      activeWorkspaceRef.current = {
        id: workspace.id,
        name: workspace.name,
        editingWorkspaceId: workspace.id,
        user_ids: workspace.members.map((member) => member.id),
      };
      onWorkspacePanelOpened();
    };

    const onWorkspaceDeleteClicked = async (rowIndex: number) => {
      const workspace: Workspace = workspaces[rowIndex];

      try {
        await apiDeleteWorkspace(workspace.id);
        deleteWorkspace(workspace);
        setSuccessNotification('Workspace is deleted');
      } catch (err) {
        setErrorNotification((err as ErrorInterface).message);
      }
    };

    const onCreateWorkspaceClicked = () => {
      setActiveWorkspace(null);
      onWorkspacePanelOpened();
    };

    EventBus.on('dashboard-settings:workspace-create', onCreateWorkspaceClicked);
    EventBus.on('dashboard-settings:workspace-edit', onWorkspaceEditClicked);
    EventBus.on('dashboard-settings:workspace-delete', onWorkspaceDeleteClicked);

    return () => {
      EventBus.off('dashboard-settings:workspace-create', onCreateWorkspaceClicked);
      EventBus.off('dashboard-settings:workspace-edit', onWorkspaceEditClicked);
      EventBus.off('dashboard-settings:workspace-delete', onWorkspaceDeleteClicked);
    };
  }, [workspaces, onWorkspacePanelOpened, deleteWorkspace, setSuccessNotification, setErrorNotification]);

  const canEditMembers = useMemo(() => {
    if (!activeWorkspace) return true;

    console.log('can edit members', isTeamWorkspace(activeWorkspace), isPrivateWorkspace(activeWorkspace));

    return !isTeamWorkspace(activeWorkspace) && !isPrivateWorkspace(activeWorkspace);
  }, [activeWorkspace]);

  return (
    <>
      <Helmet>
        <title>Journey | Workspace Settings</title>
      </Helmet>
      <DashboardSettingsManageWorkspacesList workspaces={workspaces} />
      {showWorkspacePanel && (
        <JourneySlideover
          isBedrock
          title={`${activeWorkspace ? 'Edit' : 'Create'} workspace`}
          open={showSlideOver}
          containerClassName='z-[90]'
          maxWidth='auto'
          panelClassName='w-[368px]'
          titleClassName='font-brand-serif'
          onClose={onWorkspacePanelClosed}
        >
          <DashboardSettingsManageWorkspacesForm
            currentUser={currentUser}
            workspace={activeWorkspace}
            members={currentOrganization.users ?? []}
            canEditMembers={canEditMembers}
            onSubmit={onManageWorkspaceFormSubmitted}
          />
        </JourneySlideover>
      )}
    </>
  );
};
